import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { PortalShell, type NavItem } from '../../components/PortalShell';
import { useAuth } from '../../lib/auth-context';
import { apiGet } from '../../lib/api';

// Inbox badge — count of untriaged briefs, polled every 30s so a freshly
// submitted brief lights up without a hard refresh. Soft-fails to 0 (roles
// without engagements:read get a 403, which just means no badge).
function useUnreadBriefs(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      apiGet<{ unread: number }>('/api/requests/inbox-stats')
        .then((r) => { if (!cancelled) setCount(r.unread); })
        .catch(() => { /* silent — badge is best-effort */ });
    };
    fetchCount();
    const t = window.setInterval(fetchCount, 30_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  return count;
}

// Support sidebar badge — count of unassigned tickets in `open` status,
// polled every 30s. Mirrors useUnreadBriefs: soft-fails to 0 on 403 / network
// blip so non-staff or temporarily-offline cases don't render a broken badge.
function useUnassignedSupport(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      apiGet<{ unassigned_open: number }>('/api/support/unread-stats')
        .then((r) => { if (!cancelled) setCount(r.unassigned_open); })
        .catch(() => { /* silent — badge is best-effort */ });
    };
    fetchCount();
    const t = window.setInterval(fetchCount, 30_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  return count;
}

// Compliance sidebar badge — count of clients with KYC docs awaiting review
// (rollup ∈ {in_review, rejected}). Polled every 30s. Mirrors the support
// badge: soft-fails to 0 on 403 / network blip so roles without
// compliance:read (or a transient outage) don't render a broken badge.
function useComplianceAwaiting(): number {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      apiGet<{ awaiting: number }>('/api/admin/compliance/queue-stats')
        .then((r) => { if (!cancelled) setCount(r.awaiting); })
        .catch(() => { /* silent — badge is best-effort */ });
    };
    fetchCount();
    const t = window.setInterval(fetchCount, 30_000);
    return () => { cancelled = true; window.clearInterval(t); };
  }, []);
  return count;
}

// Local nav descriptor — extends NavItem with an optional `roles` allow-list.
// When `roles` is present the entry is only rendered for those staff roles
// (mirrors PLAN §11a sidebar visibility). When absent the entry shows for all
// staff, preserving existing behaviour for every pre-existing item.
type AdminNavItem = NavItem & { roles?: readonly string[] };

// Roles that may see the Compliance entry (PLAN §11a): super/admin/lead/
// compliance + readonly (read-only). Hidden from analyst/finance.
const COMPLIANCE_NAV_ROLES = ['super', 'admin', 'lead', 'compliance', 'readonly'] as const;

const ITEMS: AdminNavItem[] = [
  { to: '/admin',           label: 'Overview',              icon: '◰', end: true },
  { to: '/admin/inbox',     label: 'Inbox',                 icon: '✉' },
  { to: '/admin/support',   label: 'Support',               icon: '?' },
  { to: '/admin/pipeline',  label: 'Pipeline',              icon: '◧' },
  { to: '/admin/clients',   label: 'Clients',               icon: '◊' },
  { to: '/admin/compliance', label: 'Compliance',           icon: '⛨', roles: COMPLIANCE_NAV_ROLES },
  { to: '/admin/proposals', label: 'Proposals',             icon: '▤' },
  { to: '/admin/finance',   label: 'Finance',               icon: '£' },
  { to: '/admin/transactions', label: 'Transactions',       icon: '⇄' },
  { to: '/admin/reports',   label: 'Reports & deliverables',icon: '▦' },
  { to: '/admin/cms',       label: 'CMS · Content',         icon: '✎' },
  { to: '/admin/users',     label: 'Users & roles',         icon: '♟' },
  { to: '/admin/audit',     label: 'Audit log',             icon: '⌘' },
  { to: '/admin/settings',  label: 'Settings',              icon: '⛭' },
];

const ROLE_LABELS: Record<string, string> = {
  super: 'Super Admin', admin: 'Admin', lead: 'Engagement Lead',
  analyst: 'Analyst', compliance: 'Compliance', finance: 'Finance', readonly: 'Read-only',
};

export function AdminLayout() {
  const { user } = useAuth();
  const unreadBriefs = useUnreadBriefs();
  const unassignedSupport = useUnassignedSupport();
  const complianceAwaiting = useComplianceAwaiting();
  const initials = (user?.initials ?? user?.name?.split(/\s+/).slice(0, 2).map((s) => s[0]).join('') ?? 'U').toUpperCase();
  const who = user?.name ?? 'Operator';
  const whoSub = `${user?.role ? ROLE_LABELS[user.role] ?? user.role : 'Staff'} · ${user?.office ?? '—'}`;
  const role = user?.role ?? '';

  // Filter by role-visibility, then inject the untriaged-brief count onto Inbox,
  // the unassigned-open support count onto Support, and the awaiting-review
  // count onto Compliance.
  const items: NavItem[] = useMemo(
    () => ITEMS
      .filter((it) => !it.roles || it.roles.includes(role))
      .map(({ roles: _roles, ...it }) => {
        if (it.to === '/admin/inbox' && unreadBriefs > 0) return { ...it, badge: unreadBriefs };
        if (it.to === '/admin/support' && unassignedSupport > 0) return { ...it, badge: unassignedSupport };
        if (it.to === '/admin/compliance' && complianceAwaiting > 0) return { ...it, badge: complianceAwaiting };
        return it;
      }),
    [unreadBriefs, unassignedSupport, complianceAwaiting, role],
  );

  return (
    <PortalShell
      who={who}
      whoSub={whoSub}
      initials={initials}
      items={items}
      signOutTo="/sign-in"
      avatarBg="#1a1a1a"
      footer={
        <div style={{ paddingTop: 14 }}>
          <div style={{ fontSize: 10, color: 'var(--fg-mute)', fontFamily: 'var(--mono)', letterSpacing: 0.06, textTransform: 'uppercase', marginBottom: 8 }}>
            Active session
          </div>
          <div style={{ fontSize: 11, color: 'var(--fg-soft)' }}>
            <strong style={{ color: 'var(--fg)', fontWeight: 500 }}>{user?.email}</strong>
          </div>
        </div>
      }
    >
      <Outlet />
    </PortalShell>
  );
}
